interface Props {
  shape: 'triangle' | 'parallelogram' | 'trapezoid' | 'rectangle';
  base?: number;
  height?: number;
  base2?: number; // second base for trapezoid
  unit?: string;
  title?: string;
}

// Area model — triangles, parallelograms, trapezoids, rectangles with base
// and height clearly marked. For triangles, shows the containing rectangle
// in dashed outline as a visual proof that A = ½ × b × h.
export default function AreaModelViz({
  shape,
  base = 8,
  height = 5,
  base2,
  unit = 'in',
  title,
}: Props) {
  const padding = 40;
  const maxW = 300;
  const maxH = 180;

  // Scale the figure so the larger of base/base2 fits maxW and height fits maxH.
  const longestBase = Math.max(base, base2 ?? 0);
  const scale = Math.min(maxW / longestBase, maxH / height);
  const B = base * scale;
  const B2 = (base2 ?? 0) * scale;
  const H = height * scale;

  const svgW = Math.max(B, B2) + padding * 2 + 20;
  const svgH = H + padding * 2;

  return (
    <div className="card p-5 space-y-3">
      <h3 className="font-display text-lg">
        {title ?? capitalize(shape)}{' '}
        <span className="text-ink/50 text-sm font-body font-normal math-display">
          base {base}
          {unit}
          {base2 !== undefined ? ` · base 2 ${base2}${unit}` : ''} · height {height}
          {unit}
        </span>
      </h3>
      <div className="flex justify-center overflow-x-auto">
        <svg
          width="100%"
          viewBox={`0 0 ${svgW} ${svgH}`}
          style={{ maxWidth: svgW, height: 'auto' }}
        >
          <g transform={`translate(${padding} ${padding})`}>
            {shape === 'triangle' && (
              <>
                {/* Dashed containing rectangle — visual proof that triangle = ½ rectangle */}
                <rect
                  x={0}
                  y={0}
                  width={B}
                  height={H}
                  fill="#eef3fa"
                  stroke="#8b95a4"
                  strokeWidth="1"
                  strokeDasharray="4 3"
                />
                <polygon
                  points={`0,${H} ${B},${H} 0,0`}
                  fill="#b693f6"
                  opacity="0.85"
                  stroke="#1b2a3a"
                  strokeWidth="1.5"
                />
              </>
            )}

            {shape === 'rectangle' && (
              <rect
                x={0}
                y={0}
                width={B}
                height={H}
                fill="#b693f6"
                opacity="0.85"
                stroke="#1b2a3a"
                strokeWidth="1.5"
              />
            )}

            {shape === 'parallelogram' && (
              <polygon
                points={`${H * 0.4},0 ${H * 0.4 + B},0 ${B},${H} 0,${H}`}
                fill="#b693f6"
                opacity="0.85"
                stroke="#1b2a3a"
                strokeWidth="1.5"
              />
            )}

            {shape === 'trapezoid' && B2 !== undefined && (
              <polygon
                points={`${(B - B2) / 2},0 ${(B - B2) / 2 + B2},0 ${B},${H} 0,${H}`}
                fill="#b693f6"
                opacity="0.85"
                stroke="#1b2a3a"
                strokeWidth="1.5"
              />
            )}

            {/* Base label — coral underline */}
            <line x1={0} y1={H + 12} x2={B} y2={H + 12} stroke="#ff7a6b" strokeWidth="2" />
            <text
              x={B / 2}
              y={H + 28}
              textAnchor="middle"
              fontSize="12"
              fontWeight="800"
              fill="#ff7a6b"
            >
              base = {base}
              {unit}
            </text>

            {/* Height label — sky */}
            <line x1={-10} y1={0} x2={-10} y2={H} stroke="#2b8bd8" strokeWidth="2" />
            <text
              x={-22}
              y={H / 2}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize="12"
              fontWeight="800"
              fill="#2b8bd8"
              transform={`rotate(-90 -22 ${H / 2})`}
            >
              h = {height}
              {unit}
            </text>

            {shape === 'trapezoid' && B2 !== undefined && (
              <text
                x={B / 2}
                y={-8}
                textAnchor="middle"
                fontSize="11"
                fontWeight="700"
                fill="#8a6d1a"
              >
                top = {base2}
                {unit}
              </text>
            )}

            {/* Caption for triangle — emphasize the ½ rectangle relationship */}
            {shape === 'triangle' && (
              <text
                x={B / 2}
                y={H / 2}
                textAnchor="middle"
                fontSize="10"
                fill="#6b7582"
              >
                (triangle is ½ the dashed rectangle)
              </text>
            )}
          </g>
        </svg>
      </div>
    </div>
  );
}

function capitalize(s: string): string {
  return s[0].toUpperCase() + s.slice(1);
}
